import { useEffect, useRef } from "react";
import { actions, runtime } from "@/controller";
import { useUISlice } from "@/models/useUISlice";
import { useAppStore } from "@/controller";
import {
  didTranscriptJustComplete,
  handleNativeTranscriptCompletion,
  resolveTranscriptBackAction,
} from "@/transcript-native";
import { i18n } from "@/locales";
import type { Transcript } from "@/types";

const confirmNotificationRationale = () =>
  new Promise<boolean>((resolve) => {
    useUISlice.getState().setAlertModal({
      title: i18n.t("transcript.notificationRationaleTitle"),
      subtitle: i18n.t("transcript.notificationRationaleSubtitle"),
      cancelText: i18n.t("transcript.notificationRationaleCancel"),
      onDismiss: () => {
        useUISlice.getState().setAlertModal(undefined);
        resolve(false);
      },
      button: {
        text: i18n.t("transcript.notificationRationaleButton"),
        onPress: () => {
          useUISlice.getState().setAlertModal(undefined);
          resolve(true);
        },
      },
    });
  });

export const TranscriptNativeCapabilities = ({
  transcriptId,
  transcriptStatus,
  navigateHome,
  showBottomMenu,
  setShowBottomMenu,
  showMore,
  setShowMore,
}: {
  transcriptId?: string;
  transcriptStatus?: Transcript["status"];
  navigateHome: () => void;
  showBottomMenu: boolean;
  setShowBottomMenu: (open: boolean) => void;
  showMore: boolean;
  setShowMore: (open: boolean) => void;
}) => {
  const previousCompletion = useRef({ transcriptId, status: transcriptStatus });
  const paywallVisible = useUISlice((state) => state.paywallVisible);
  const modal = useUISlice((state) => state.alertModal);
  const prompt = useUISlice((state) => state.alertPrompt);
  const searchWidget = useUISlice((state) => state.alertWidgetSearch);
  const languagePicker = useUISlice((state) => state.alertLanguagePicker);
  const exportOptions = useUISlice(
    (state) => state.alertExportTranscriptOptions,
  );

  useEffect(() => {
    const currentCompletion = { transcriptId, status: transcriptStatus };
    const justCompleted = didTranscriptJustComplete(
      previousCompletion.current,
      currentCompletion,
    );
    previousCompletion.current = currentCompletion;
    if (!runtime.isNative || !transcriptId || transcriptStatus !== "transcription_completed") return;
    if (!justCompleted && useAppStore.getState().postTranscriptionReviewRequested) return;

    const timer = setTimeout(() => void handleNativeTranscriptCompletion({
      notifications: runtime.notifications,
      wasPermissionRequested: () =>
        Boolean(useAppStore.getState().notificationPermissionRequested),
      markPermissionRequested: () =>
        useAppStore.getState().setNotificationPermissionRequested(true),
      confirmPermissionRationale: confirmNotificationRationale,
      review: { request: async () => {
        const result = await actions.app.requestTranscriptionReview(transcriptId);
        return result.ok && result.value.requested;
      } },
    }), 1500);
    return () => clearTimeout(timer);
  }, [transcriptId, transcriptStatus]);

  useEffect(() => {
    if (!runtime.isNative) return;
    let disposed = false;
    let attached: { remove: () => Promise<void> } | undefined;
    void runtime.app
      .onBackButton(() => {
        const ui = useUISlice.getState();
        const action = resolveTranscriptBackAction({
          paywallVisible: Boolean(paywallVisible),
          modalVisible: Boolean(modal && !modal.hideDismissIcon),
          promptVisible: Boolean(prompt),
          searchVisible: Boolean(searchWidget),
          exportVisible: Boolean(exportOptions),
          languageVisible: Boolean(languagePicker),
          moreVisible: showMore,
          bottomMenuVisible: showBottomMenu,
        });
        switch (action) {
          case "blockedByPaywall":
            return;
          case "dismissModal": {
            const activeModal = ui.alertModal;
            if (activeModal?.onDismiss) activeModal.onDismiss();
            else ui.setAlertModal(undefined);
            return;
          }
          case "dismissPrompt":
            ui.setAlertPrompt(undefined);
            return;
          case "dismissSearch":
            ui.setAlertWidgetSearch(undefined);
            return;
          case "dismissExport":
            ui.setAlertExportTranscriptOptions(undefined);
            return;
          case "dismissLanguage":
            ui.setAlertLanguagePicker(undefined);
            return;
          case "dismissMore":
            setShowMore(false);
            return;
          case "dismissBottomMenu":
            setShowBottomMenu(false);
            return;
          case "navigateHome":
            navigateHome();
        }
      })
      .then((listener) => {
        if (disposed) void listener.remove();
        else attached = listener;
      });

    return () => {
      disposed = true;
      void attached?.remove();
    };
  }, [
    exportOptions,
    languagePicker,
    modal,
    navigateHome,
    paywallVisible,
    prompt,
    searchWidget,
    setShowBottomMenu,
    setShowMore,
    showBottomMenu,
    showMore,
  ]);

  return null;
};
